import {
  Button,
  Card,
  cn,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  SegmentedControl,
} from '@up2stay/up2promo-ui';
import { MessageSquare, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { ListSearchField } from '@/core/components/layout';
import { PageHeader } from '@/core/components/page-header';
import {
  ReputationLayout,
  ReputationPageBanners,
} from '@/core/components/reputation/reputation-layout';
import { ReputationPageBreadcrumb } from '@/core/components/reputation/reputation-page-breadcrumb';
import { ReviewDetail } from '@/core/components/reputation/reviews/review-detail';
import { ReviewsFilterChips } from '@/core/components/reputation/reviews/reviews-filter-chips';
import type { ReviewsInboxTab } from '@/core/components/reputation/reviews/reviews-filters';
import { ReviewsInboxHeader } from '@/core/components/reputation/reviews/reviews-inbox-header';
import { ReviewsListPane } from '@/core/components/reputation/reviews/reviews-list-pane';
import { useReviewsInbox } from '@/core/components/reputation/reviews/use-reviews-inbox';
import { useIsMobile, useShellSidebarOverlay } from '@/core/hooks/use-mobile';
import { useDictionary } from '@/core/providers/dictionary-provider';

export type { ReviewsInboxFocusState } from '@/core/components/reputation/reviews/reviews-inbox-url';

export function ReviewsPage() {
  return (
    <AppIonPage>
      <AppPageContent>
        <ReputationLayout>
          <ReviewsPageContent />
        </ReputationLayout>
      </AppPageContent>
    </AppIonPage>
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable ||
      ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
  );
}

function ReviewsPageContent() {
  const dict = useDictionary();
  const inbox = useReviewsInbox();
  const { reviewsDict, stats, filters, activeReview } = inbox;
  // Below the shell-sidebar breakpoint there is no room for two panes.
  const split = !useShellSidebarOverlay();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Empty inbox (no reviews / no matches): one full-width empty state, no detail pane.
  const hasRows =
    inbox.reviewsLoading ||
    inbox.reviews.length > 0 ||
    !inbox.isSyncCompleteForScope;
  const isMobile = useIsMobile();

  const { selectRelative } = inbox;
  useEffect(() => {
    if (!split) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      if (event.key === 'j') selectRelative(1);
      else if (event.key === 'k') selectRelative(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [split, selectRelative]);

  const count = (n: number) =>
    stats.scoped ? ` ${n.toLocaleString(inbox.locale)}` : '';
  const tabOptions: { value: ReviewsInboxTab; label: React.ReactNode }[] = [
    {
      value: 'open',
      label: (
        <>
          <span className="md:hidden">{reviewsDict.tabs.openShort}</span>
          <span className="max-md:hidden">{reviewsDict.tabs.open}</span>
          <span className="text-muted-foreground font-medium">
            {count(stats.unanswered)}
          </span>
        </>
      ),
    },
    {
      value: 'answered',
      label: (
        <>
          {reviewsDict.tabs.answered}
          <span className="text-muted-foreground font-medium">
            {count(stats.answered)}
          </span>
        </>
      ),
    },
    {
      value: 'all',
      label: (
        <>
          {reviewsDict.tabs.all}
          <span className="text-muted-foreground font-medium">
            {count(stats.total)}
          </span>
        </>
      ),
    },
  ];

  return (
    <AppPageLayout>
      <PageHeader
        title={reviewsDict.title}
        actions={
          <>
            <ReviewsInboxHeader inbox={inbox} mode="header" />
            <Button
              variant="outline"
              size="icon"
              mode="icon"
              className="size-9 md:hidden"
              aria-label={reviewsDict.list.search}
              aria-pressed={searchOpen}
              onClick={() => {
                if (searchOpen) {
                  setSearchOpen(false);
                  return;
                }
                // Render the field, then focus it inside the tap so the
                // keyboard opens on iOS too (DEV-771).
                flushSync(() => setSearchOpen(true));
                searchInputRef.current?.focus();
              }}
            >
              <Search className="size-5" aria-hidden />
            </Button>
          </>
        }
        eyebrow={
          stats.scoped
            ? reviewsDict.stats.summary
                .replace('{unanswered}', String(stats.unanswered))
                .replace('{critical}', String(stats.critical))
            : undefined
        }
        breadcrumb={<ReputationPageBreadcrumb pageLabel={dict.nav.reviews} />}
      />
      <ReputationPageBanners />

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <ReviewsInboxHeader inbox={inbox} mode="inline" />

        <div className="flex flex-wrap items-center gap-2.5">
          <SegmentedControl
            ariaLabel={reviewsDict.tabs.label}
            value={filters.tab}
            onChange={inbox.setTab}
            options={tabOptions}
            fullWidth={isMobile}
            className="max-w-full overflow-x-auto"
          />
          <ReviewsFilterChips
            value={filters}
            onChange={inbox.onFiltersChange}
            locationOptions={inbox.locations.map((loc) => ({
              id: loc.id,
              name: loc.name ?? loc.id,
            }))}
            aspectOptions={inbox.aspectOptions}
            labels={reviewsDict.filterChips}
            sentimentLabels={{
              positive: reviewsDict.sentiment.positive,
              neutral: reviewsDict.sentiment.neutral,
              negative: reviewsDict.sentiment.negative,
            }}
          />
          <ListSearchField
            className={cn(
              'w-full sm:ml-auto sm:w-64',
              !searchOpen && 'max-md:hidden',
            )}
            inputRef={searchInputRef}
            placeholder={reviewsDict.list.search}
            value={inbox.searchQuery}
            onChange={(e) => inbox.onSearchChange(e.target.value)}
            onClear={() => inbox.onSearchChange('')}
          />
        </div>

        <Card
          data-split={hasRows}
          className="grid min-w-0 max-xl:border-0 max-xl:bg-transparent max-xl:shadow-none xl:min-h-[34rem] xl:overflow-hidden xl:h-[calc(100dvh-19rem)] xl:min-h-[34rem] xl:data-[split=true]:grid-cols-[minmax(340px,400px)_minmax(0,1fr)]"
        >
          <ReviewsListPane
            inbox={inbox}
            cards={!split}
            className={cn('min-w-0', hasRows && 'xl:border-r')}
            onSelect={(review) => {
              inbox.setActiveReviewId(review.id);
              if (!split) setDrawerOpen(true);
            }}
          />
          {split && hasRows ? (
            <div className="flex min-h-0 min-w-0 flex-col">
              <div className="text-muted-foreground flex shrink-0 items-center gap-2 border-b px-5 py-3 text-xs">
                <kbd className="rounded border px-1.5 font-mono">J</kbd>
                <kbd className="rounded border px-1.5 font-mono">K</kbd>
                {reviewsDict.detail.navigate}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 sm:px-7">
                {activeReview ? (
                  <ReviewDetail
                    key={activeReview.id}
                    inbox={inbox}
                    review={activeReview}
                  />
                ) : (
                  <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-2 text-sm">
                    <MessageSquare className="size-6" aria-hidden />
                    {reviewsDict.detail.selectPrompt}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </Card>
      </div>

      {!split ? (
        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DrawerContent
            fitContent={false}
            className="h-[min(92dvh,760px)] max-h-[min(92dvh,760px)]"
          >
            <DrawerHeader className="sr-only">
              <DrawerTitle>{activeReview?.authorName}</DrawerTitle>
              <DrawerDescription>
                {reviewsDict.detail.selectPrompt}
              </DrawerDescription>
            </DrawerHeader>
            <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-6 pt-2">
              {activeReview ? (
                <ReviewDetail
                  key={activeReview.id}
                  inbox={inbox}
                  review={activeReview}
                  onPublished={() => setDrawerOpen(false)}
                />
              ) : null}
            </div>
          </DrawerContent>
        </Drawer>
      ) : null}
    </AppPageLayout>
  );
}
